import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Flame, Coins, ArrowUpRight } from "lucide-react";
import { api } from "@/api/client";
import { cryptoAvailable } from "@/lib/vaultCrypto";

// Compact "Security & Rewards Health" trust strip for the top of the Profile view.
// 3 live indicators: AES-256 vault status (Web Crypto), login streak, cash-out
// eligible earned credits. Read-only; consumes the existing /rewards/conversion feed.
export default function SecurityHealthBadge({ onOpenWealth }) {
  const [state, setState] = useState(null);
  const aesActive = cryptoAvailable();

  useEffect(() => {
    let alive = true;
    api.get("/rewards/conversion")
      .then((r) => { if (alive) setState(r.data?.state || {}); })
      .catch(() => { if (alive) setState({}); });
    return () => { alive = false; };
  }, []);

  const streak = state ? (state.activeStreakDays ?? state.loginStreak ?? 0) : null;
  const earned = state ? (Number(state.earnedCredits) || 0) : null;
  const fmt = (n) => (n == null ? "—" : n % 1 === 0 ? String(n) : n.toFixed(2));

  return (
    <button
      type="button"
      onClick={onOpenWealth}
      data-testid="security-health-badge"
      aria-label="Open Wealth rewards & conversion"
      className="mb-3 w-full flex items-stretch gap-1.5 rounded-2xl border-2 border-charcoal bg-charcoal p-1.5 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform"
    >
      {/* AES-256 vault status */}
      <div
        data-testid="health-aes-pill"
        className={`flex flex-1 min-w-0 items-center gap-1.5 rounded-xl border-2 px-2 py-1.5 ${
          aesActive ? "bg-emerald-500/15 border-emerald-500" : "bg-red-500/15 border-red-500"
        }`}
      >
        {aesActive ? (
          <ShieldCheck size={16} strokeWidth={2.6} className="shrink-0 text-emerald-400" data-testid="health-aes-ok" />
        ) : (
          <ShieldAlert size={16} strokeWidth={2.6} className="shrink-0 text-red-400" />
        )}
        <span className="leading-none min-w-0">
          <span className="block font-mono text-[7.5px] uppercase tracking-[0.12em] text-white/50">Vault</span>
          <span className="block font-mono text-[9px] font-bold uppercase tracking-[0.04em] text-white truncate">
            {aesActive ? "AES-256 Active" : "Unavailable"}
          </span>
        </span>
      </div>

      {/* Streak */}
      <div
        data-testid="health-streak-pill"
        className="flex items-center gap-1.5 rounded-xl border-2 border-gold bg-gold/15 px-2 py-1.5"
      >
        <Flame size={16} strokeWidth={2.6} className="shrink-0 text-gold" />
        <span className="leading-none">
          <span className="block font-mono text-[7.5px] uppercase tracking-[0.12em] text-white/50">Streak</span>
          <span className="block font-mono text-[11px] font-black text-gold" data-testid="health-streak-value">
            {streak == null ? "—" : `${streak}d`}
          </span>
        </span>
      </div>

      {/* Earned (cash-out eligible) credits — primary tap affordance */}
      <div
        data-testid="health-earned-pill"
        className="flex items-center gap-1.5 rounded-xl border-2 border-cyan-400 bg-cyan-400/15 px-2 py-1.5"
      >
        <Coins size={16} strokeWidth={2.6} className="shrink-0 text-cyan-300" />
        <span className="leading-none">
          <span className="block font-mono text-[7.5px] uppercase tracking-[0.12em] text-white/50">Earned</span>
          <span className="block font-mono text-[11px] font-black text-cyan-200" data-testid="health-earned-value">
            {fmt(earned)}
          </span>
        </span>
        <ArrowUpRight size={13} strokeWidth={2.8} className="shrink-0 text-cyan-300/80 -ml-0.5" />
      </div>
    </button>
  );
}
